import axios from 'axios';
import axiosRetry from 'axios-retry';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';

import { PasswordRequirements } from 'components/PasswordField/types';
import { DEFAULT_PASSWORD_REQUIREMENTS } from 'components/PasswordField/utils';
import { reportSentryError } from 'sentry';
import { API_ENDPOINTS, ROUTES } from 'utils/constants';

export type PasswordRequirementsAPI = {
  email?: string;
  is_password_complex: boolean;
  password_min_length: number;
};

axiosRetry(axios, {
  retries: 3,
  retryCondition: (error) =>
    axiosRetry.isNetworkOrIdempotentRequestError(error) ||
    error.response?.status === 422 ||
    error.response?.status === 500
});

export const usePasswordRequirements = (
  token: string
): {
  passwordRequirements: PasswordRequirements;
  email: string;
  isExpiredOrInvalidToken: boolean;
} => {
  const [, { language }] = useTranslation();
  const history = useHistory();
  const { search } = useLocation();
  const [{ email, ...passwordRequirements }, setPasswordRequirements] =
    React.useState<PasswordRequirements & { email: string }>({
      ...DEFAULT_PASSWORD_REQUIREMENTS,
      email: ''
    });
  const [isExpiredOrInvalidToken, setIsExpiredOrInvalidToken] =
    React.useState(false);

  React.useEffect(() => {
    axios
      .get<PasswordRequirementsAPI>(API_ENDPOINTS.PASSWORD_REQUIREMENTS, {
        params: {
          token,
          locale: language
        }
      })
      .then(({ data }) => {
        let {
          is_password_complex: isComplex,
          password_min_length: minLength,
          email: userEmail
        } = data;
        if (isComplex === undefined || typeof isComplex !== 'boolean') {
          reportSentryError('[API]: Malformed password requirements', {
            level: 'error',
            extra: {
              query: 'password-requirements',
              field: 'is_password_complex',
              data
            }
          });
          isComplex = DEFAULT_PASSWORD_REQUIREMENTS.isComplex;
        }

        if (minLength === undefined || typeof minLength !== 'number') {
          reportSentryError('[API]: Malformed password requirements', {
            level: 'error',
            extra: {
              query: 'password-requirements',
              field: 'password_min_length',
              data
            }
          });
          minLength = DEFAULT_PASSWORD_REQUIREMENTS.minLength;
        }

        if (userEmail === undefined || typeof userEmail !== 'string') {
          reportSentryError('[API]: Malformed password requirements', {
            level: 'error',
            extra: {
              query: 'password-requirements',
              field: 'email',
              data
            }
          });
          userEmail = '';
        }

        setPasswordRequirements({
          isComplex,
          minLength,
          email: userEmail
        });
      })
      .catch((error) => {
        reportSentryError(error, {
          level: 'error',
          extra: {
            query: 'password-requirements'
          }
        });
        if (error?.response?.data?.error === 'not_found') {
          history.push(`/${language}/${ROUTES.FORGOT_PASSWORD}${search}`);
          setIsExpiredOrInvalidToken(true);
        }
        console.error('Failed to load password requirements!');
      });
  }, [token, language]);

  return {
    passwordRequirements,
    email,
    isExpiredOrInvalidToken
  };
};
